import { useMemo, useState, useEffect } from "react";
import "./Volby.css";
import { Helmet } from "react-helmet-async";
import { Reveal, MapaCR, Button, colors } from "@ceskastranaasocialu/komponenty";
import volbyNadpis from "../../images/Titles/web_volby_nadpis.webp";
import { programEntries, kandidatiKomunalni, kandidatiSenatni, kandidatiKrajske, kandidatiEvropske, kandidatiCelostatni } from "./data";
const nazvyVoleb = {
    komunalni: "Komunální volby",
    senatni: "Senátní volby",
    krajske: "Krajské volby",
    evropske: "Evropské volby",
    celostatni: "Celostátní volby"
};
const years = Array.from(
    new Set([
        ...Object.keys(programEntries),
        ...Object.keys(kandidatiKomunalni),
        ...Object.keys(kandidatiSenatni),
        ...Object.keys(kandidatiKrajske),
        ...Object.keys(kandidatiEvropske),
        ...Object.keys(kandidatiCelostatni)
    ])
).sort((a, b) => Number(b) - Number(a));
const sekceMeta = {
    program: { label: "Program" },
    komunalni: { label: "Komunální volby" },
    senatni: { label: "Senátní volby" },
    krajske: { label: "Krajské volby" },
    evropske: { label: "Evropské volby" },
    celostatni: { label: "Celostátní volby" }
};
const datyKrajovePodleSekce = {
    komunalni: kandidatiKomunalni,
    senatni: kandidatiSenatni,
    krajske: kandidatiKrajske
};

const formatHlasy = (n) => (n == null ? "–" : Number(n).toLocaleString("cs-CZ"));
const formatProcenta = (n) => (n == null ? "–" : `${Number(n).toLocaleString("cs-CZ")} %`);
function maNejakeKandidatyObce(data) {
    if (!data) return false;
    return Object.keys(data).some((kraj) =>
        Object.keys(data[kraj] || {}).some((obec) => {
            const zaznam = data[kraj][obec];
            return (Array.isArray(zaznam) ? zaznam : zaznam?.kandidati || []).length > 0;
        })
    );
}
function maNejakeKandidatyKraje(data) {
    if (!data) return false;
    return Object.keys(data).some((kraj) => (data[kraj] || []).length > 0);
}
function dostupneSekce(year) {
    const sekce = [];
    if (programEntries[year] && programEntries[year].length > 0) sekce.push("program");
    if (maNejakeKandidatyObce(kandidatiKomunalni[year])) sekce.push("komunalni");
    if (maNejakeKandidatyObce(kandidatiSenatni[year])) sekce.push("senatni");
    if (maNejakeKandidatyObce(kandidatiKrajske[year])) sekce.push("krajske");
    if (kandidatiEvropske[year] && kandidatiEvropske[year].length > 0) sekce.push("evropske");
    if (maNejakeKandidatyKraje(kandidatiCelostatni[year])) sekce.push("celostatni");
    return sekce;
}
function KandidatStrana({ kand }) {
    if (kand.csa) return <span className="volbyKandidatStrana">ČSA</span>;
    if (kand.strana) return <span className="volbyKandidatJinaStrana">{kand.strana}</span>;
    return <span className="volbyKandidatNestranik">nestraník</span>;
}
function KandidatRadek({ kand, klic, otevreno, prepnout }) {
    const maInfo = !!kand.info;
    const jeOtevreny = otevreno === klic;
    return (
        <div className="volbyKandidat">
            <div className="volbyKandidatRadek">
                {kand.cislo != null && <span className="volbyKandidatCislo">{kand.cislo}.</span>}
                <span
                    className={`volbyKandidatJmeno${maInfo ? " volbyKandidatJmenoKlikatelne" : ""}`}
                    onClick={maInfo ? () => prepnout(jeOtevreny ? null : klic) : undefined}
                    role={maInfo ? "button" : undefined}
                    tabIndex={maInfo ? 0 : undefined}
                    onKeyDown={
                        maInfo
                            ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      prepnout(jeOtevreny ? null : klic);
                                  }
                              }
                            : undefined
                    }
                >
                    {kand.jmeno}
                </span>
                <KandidatStrana kand={kand} />
                {kand.hlasy !== undefined && (
                    <span className="volbyKandidatHlasy">{formatHlasy(kand.hlasy)} hlasů</span>
                )}
            </div>
            {maInfo && jeOtevreny && (
                <ul className="volbyKandidatInfo">
                    {(Array.isArray(kand.info) ? kand.info : [kand.info]).map((radek, j) => (
                        <li key={j}>{radek}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}
function Kandidati({ data, kraj }) {
    const [otevrenyKandidat, setOtevrenyKandidat] = useState(null);
    const kraje = kraj ? (data[kraj] ? [kraj] : []) : Object.keys(data);
    if (kraje.length === 0) {
        return null;
    }
    return (
        <div className="volbyKandidati">
            {kraje.map((k) => {
                const obce = data[k] || {};
                const nazvyObci = Object.keys(obce);
                const nazevKraje = k === "Praha" ? "Praha" : k === "Vysočina" ? "Kraj Vysočina" : `${k} kraj`;
                return (
                    <section key={k} className="volbyKraj">
                        <p className="volbyKrajNazev">{nazevKraje}</p>
                        <div className="volbyObce">
                            {nazvyObci.map((obec) => {
                                const zaznam = obce[obec];
                                const seznam = Array.isArray(zaznam) ? zaznam : zaznam?.kandidati || [];
                                const serazeni = [...seznam].sort((a, b) => (a.cislo ?? Infinity) - (b.cislo ?? Infinity));
                                const vysl = !Array.isArray(zaznam) ? zaznam : null;
                                return (
                                    <div key={`${k}-${obec}`} className="volbyObec">
                                        <p className="volbyObecNazev">{obec}</p>
                                        {vysl && (
                                            <p className="volbyObecVysledek">
                                                Hlasů: <strong>{formatHlasy(vysl.hlasy)}</strong> · <strong>{formatProcenta(vysl.procenta)}</strong>
                                            </p>
                                        )}
                                        {serazeni.map((kand, i) => (
                                            <KandidatRadek
                                                key={`${k}-${obec}-${i}`}
                                                kand={kand}
                                                klic={`${k}-${obec}-${i}`}
                                                otevreno={otevrenyKandidat}
                                                prepnout={setOtevrenyKandidat}
                                            />
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
function KandidatiCelostatni({ data, kraj }) {
    const [otevrenyKandidat, setOtevrenyKandidat] = useState(null);
    const kraje = kraj ? (data[kraj] ? [kraj] : []) : Object.keys(data);
    if (kraje.length === 0) {
        return null;
    }
    return (
        <div className="volbyKandidati">
            {kraje.map((k) => {
                const serazeni = [...(data[k] || [])].sort((a, b) => (a.cislo ?? Infinity) - (b.cislo ?? Infinity));
                const nazevKraje = k === "Praha" ? "Praha" : k === "Vysočina" ? "Kraj Vysočina" : `${k} kraj`;
                return (
                    <section key={k} className="volbyKraj">
                        <p className="volbyKrajNazev volbyKrajNazevCelostatni">{nazevKraje}</p>
                        <div className="volbyObce">
                            <div className="volbyObec">
                                {serazeni.map((kand, i) => (
                                    <KandidatRadek
                                        key={`${k}-${i}`}
                                        kand={kand}
                                        klic={`${k}-${i}`}
                                        otevreno={otevrenyKandidat}
                                        prepnout={setOtevrenyKandidat}
                                    />
                                ))}
                            </div>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
function KandidatiEvropske({ data }) {
    const [otevrenyKandidat, setOtevrenyKandidat] = useState(null);
    if (!data || data.length === 0) return null;
    const serazeni = [...data].sort((a, b) => (a.cislo ?? Infinity) - (b.cislo ?? Infinity));
    return (
        <div className="volbyKandidati">
            <div className="volbyObec">
                {serazeni.map((kand, i) => (
                    <KandidatRadek
                        key={`evropske-${i}`}
                        kand={kand}
                        klic={`evropske-${i}`}
                        otevreno={otevrenyKandidat}
                        prepnout={setOtevrenyKandidat}
                    />
                ))}
            </div>
        </div>
    );
}
export default function Volby() {
    const [year, setYear] = useState(years[0]);
    const [vybranýKraj, setVybranýKraj] = useState(null);
    const [sekce, setSekce] = useState(dostupneSekce(years[0])[0]);
    const sekceProRok = useMemo(() => dostupneSekce(year), [year]);
    useEffect(() => {
        if (!sekceProRok.includes(sekce)) {
            setSekce(sekceProRok[0]);
        }
    }, [year, sekceProRok, sekce]);
    const zmenitRok = (y) => {
        setYear(y);
        setVybranýKraj(null);
    };
    const zmenitSekci = (s) => {
        setSekce(s);
        setVybranýKraj(null);
    };
    const jeKrajovaSekce = sekce === "komunalni" || sekce === "senatni" || sekce === "krajske";
    const jeCelostatniSekce = sekce === "celostatni";
    const aktivniData = useMemo(
        () => (jeKrajovaSekce ? datyKrajovePodleSekce[sekce][year] || {} : {}),
        [jeKrajovaSekce, sekce, year]
    );
    const celostatniData = useMemo(
        () => (jeCelostatniSekce ? kandidatiCelostatni[year] || {} : {}),
        [jeCelostatniSekce, year]
    );
    const evropskaData = kandidatiEvropske[year] || [];
    const pocty = useMemo(() => {
        const m = {};
        if (jeKrajovaSekce) {
            for (const kraj of Object.keys(aktivniData)) {
                let n = 0;
                for (const obec of Object.keys(aktivniData[kraj])) {
                    const zaznam = aktivniData[kraj][obec];
                    n += (Array.isArray(zaznam) ? zaznam : zaznam?.kandidati || []).length;
                }
                if (n > 0) m[kraj] = n;
            }
        } else if (jeCelostatniSekce) {
            for (const kraj of Object.keys(celostatniData)) {
                const n = (celostatniData[kraj] || []).length;
                if (n > 0) m[kraj] = n;
            }
        }
        return m;
    }, [aktivniData, celostatniData, jeKrajovaSekce, jeCelostatniSekce]);
    const maKandidaty = Object.keys(pocty).length > 0;
    const vybrat = (kraj) => setVybranýKraj((prev) => (prev === kraj ? null : kraj));
    return (
        <div id="volbyContent">
            <Helmet>
                <title>Volby · ČSA</title>
                <meta name="description" content="Kde a s kým ČSA kandiduje. Vyber rok voleb, klikni na kraj v mapě a podívej se na naše kandidáty po obcích — a na celý volební program." />
                <meta property="og:title" content="Volby · ČSA" />
                <meta property="og:description" content="Kde a s kým ČSA kandiduje. Vyber rok voleb, klikni na kraj v mapě a podívej se na naše kandidáty po obcích — a na celý volební program." />
                <meta property="og:image" content={`${process.env.PUBLIC_URL}/assets/banner.png`} />
                <meta property="og:url" content="https://www.asocialove.cz/volby" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Volby · ČSA" />
                <meta name="twitter:description" content="Kde a s kým ČSA kandiduje. Vyber rok voleb, klikni na kraj v mapě a podívej se na naše kandidáty po obcích — a na celý volební program." />
                <meta name="twitter:image" content={`${process.env.PUBLIC_URL}/assets/banner.png`} />
            </Helmet>
            <div className="volbyTitle">
                <img src={volbyNadpis} draggable="false" alt="" />
            </div>
            <div style={{ maxWidth: 1200, margin: "24px auto 0", padding: "0 16px" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                    {years.map((y) => {
                        const active = year === y;
                        return (
                            <Button key={y} variant={active ? "primary" : "dark"} onClick={() => zmenitRok(y)} style={{ fontFamily: "inherit", fontSize: 18, padding: "10px 22px" }}>
                                {y}
                            </Button>
                        );
                    })}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 12 }}>
                    {sekceProRok.map((s) => {
                        const active = sekce === s;
                        return (
                            <Button key={s} variant={active ? "primary" : "dark"} onClick={() => zmenitSekci(s)} style={{ fontFamily: "inherit", fontSize: 18, padding: "10px 22px" }}>
                                {sekceMeta[s].label}
                            </Button>
                        );
                    })}
                </div>
            </div>
            {nazvyVoleb[year] && <h1 className="volbyNazevVoleb">{`${nazvyVoleb[year]} ${year}`}</h1>}
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px" }}>
                {jeKrajovaSekce && (
                    <>
                        {maKandidaty && <MapaCR className="mapaCR" vybrano={vybranýKraj} pocty={pocty} onVybrat={vybrat} />}
                        <Kandidati data={aktivniData} kraj={vybranýKraj} />
                    </>
                )}
                {jeCelostatniSekce && (
                    <>
                        {maKandidaty && <MapaCR className="mapaCR" vybrano={vybranýKraj} pocty={pocty} onVybrat={vybrat} />}
                        <KandidatiCelostatni data={celostatniData} kraj={vybranýKraj} />
                    </>
                )}
                {sekce === "evropske" && <KandidatiEvropske data={evropskaData} />}
                {sekce === "program" && (
                    <>
                        <p id="program" className="volbySekceNadpis">
                            {`Program ${year}`}
                        </p>
                        <div key={year} style={{ marginTop: 16, columns: "min(100%, 500px) auto", columnGap: 24, paddingBottom: 64 }}>
                            {programEntries[year].map((item, i) => (
                                <Reveal key={`${year}-${i}`} delay={Math.min(i * 0.03, 0.3)}>
                                    <div
                                        style={{ breakInside: "avoid", padding: "16px 20px", marginBottom: 12, background: "white", color: colors.dark, borderRadius: 6, border: `2px solid ${colors.dark}`, boxShadow: `3px 3px 0 ${colors.dark}`, display: "flex", gap: 12, alignItems: "flex-start", transition: "all 0.2s", fontSize: 18 }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translate(-2px,-2px)";
                                            e.currentTarget.style.boxShadow = `5px 5px 0 ${colors.cerise}`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "none";
                                            e.currentTarget.style.boxShadow = `3px 3px 0 ${colors.dark}`;
                                        }}
                                    >
                                        <span style={{ fontWeight: 700, fontSize: 22, color: colors.cerise, minWidth: 32 }}>{String(i + 1).padStart(2, "0")}</span>
                                        <span style={{ lineHeight: 1.5 }}>{item}</span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
